import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useCategories } from "@/hooks/useCategories";
import ListingGrid from "@/components/listings/ListingGrid";
import Seo from "@/components/common/Seo";
import HeroCarousel from "@/components/home/HeroCarousel";
import ActiveSessionBanner from "@/components/engage/ActiveSessionBanner";
import FeaturedRail from "@/components/home/FeaturedRail";
import RecentlyViewedRail from "@/components/home/RecentlyViewedRail";
import EventsRow from "@/components/home/EventsRow";
import SectionHeader from "@/components/home/SectionHeader";
import CircularTileCarousel from "@/components/home/CircularTileCarousel";
import VehicleIntelligenceBanner from "@/components/home/VehicleIntelligenceBanner";
import NewsletterBand from "@/components/home/NewsletterBand";
import { HOME } from "@/constants/testIds";

// Sections in the same order as the live allsale.co.nz homepage.
const SECTIONS = [
  { slug: "property", title: "Property" },
  { slug: "cars", title: "Cars" },
  { slug: "jobs", title: "Jobs" },
  { slug: "fashion", title: "Fashion" },
  { slug: "books-sports-hobbies-supplies", title: "Books, Sports, Hobbies and Supplies" },
  { slug: "pets", title: "Pets" },
  { slug: "service", title: "Services" },
];

function useListingsFor(slug) {
  return useQuery({
    queryKey: ["home-listings", slug],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/listings", {
        // Legacy-layout parity: one clean row of 4 cards per section.
        params: { country: "NZ", category: slug, limit: 4, sort: "recent" },
      });
      return data.listings || [];
    },
    staleTime: 2 * 60_000,
  });
}

export default function HomePage() {
  const { data: allCategories = [] } = useCategories();
  // Homepage tiles carousel is a browse strip — filter out Create Deal /
  // Create Event which are post shortcuts (they live on /sell instead).
  const categories = allCategories.filter((c) => (c.type || "ad") === "ad");

  const categoryBySlug = useMemo(() => {
    const map = {};
    for (const c of categories) map[c.slug] = c;
    return map;
  }, [categories]);

  return (
    <>
      <Seo
        url="https://allsale.co.nz/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Allsale Classified",
          url: "https://allsale.co.nz",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://allsale.co.nz/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />

      {/* SEO: home has no visible page heading, so expose exactly one H1 to crawlers */}
      <h1 className="sr-only">Allsale Classified — New Zealand&apos;s premium classifieds</h1>

      {/* Iter 695 — sticky "unfinished check-in" resume banner (shows only
          while the customer has a live engage session). */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-3 empty:hidden">
        <ActiveSessionBanner />
      </div>

      {/* Hero rotation — Iter 654: legacy home carousel, admin-managed
          banners only (the prio-30 "FREE Registration & Listing" creative
          replaces the old inline signup slide). */}
      <HeroCarousel />

      {/* Events row — legacy parity: live events sit right under the
          hero, above everything else (hidden while the API has none). */}
      <EventsRow />

      {/* Paid Boost featured rail — hidden when empty */}
      <FeaturedRail />

      {/* Recently-viewed rail — client-only, hidden when empty */}
      <RecentlyViewedRail />

      {/* Main categories — circular tile carousel */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-6" data-testid={HOME.categoryGrid}>
        <SectionHeader title="Categories" seeAllHref="/search" testId="section-categories" />
        <CircularTileCarousel
          items={categories.map((c) => ({
            id: c.id,
            name: c.name,
            slug: c.slug,
            icon: c.icon,
            emoji: c.emoji,
            thumbnail_url: c.thumbnail_url,
          }))}
          hrefBuilder={(c) => `/c/${c.slug}`}
          ariaLabel="All categories"
          testId="main-category-tiles"
        />
      </section>

      {SECTIONS.map((sec, index) => {
        const rootCat = categoryBySlug[sec.slug];
        const subTiles = (rootCat?.children || []).map((sc) => ({
          id: sc.id, name: sc.name, slug: sc.slug, icon: sc.icon || rootCat?.icon,
          emoji: sc.emoji, thumbnail_url: sc.thumbnail_url,
        }));

        return (
          <React.Fragment key={sec.slug}>
            {/* Subcategory tiles (only if this root has children) */}
            {subTiles.length > 0 && (
              <section className="max-w-7xl mx-auto px-4 md:px-8 py-4">
                <CircularTileCarousel
                  items={(rootCat?.children || []).map((sc) => ({
                    id: sc.id,
                    name: sc.name,
                    slug: sc.slug,
                    icon: sc.icon || rootCat?.icon,
                    emoji: sc.emoji || rootCat?.emoji,
                    thumbnail_url: sc.thumbnail_url,
                  }))}
                  hrefBuilder={(c) => `/search?category=${c.slug}`}
                  ariaLabel={`${sec.title} subcategories`}
                  testId={`sub-tiles-${sec.slug}`}
                />
              </section>
            )}

            {/* Section listings row */}
            <SectionListings slug={sec.slug} title={sec.title} />

            {/* Vehicle Intelligence banner slotted after Cars */}
            {sec.slug === "cars" && <VehicleIntelligenceBanner />}
          </React.Fragment>
        );
      })}

      {/* Newsletter band */}
      <NewsletterBand />

      {/* Iter-290 §1a — Become a Partner Store banner. Visible to
          everyone including guests. */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-4" data-testid="home-partner-band">
        <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-[#0F70A8] p-8 md:p-12 text-white relative overflow-hidden">
          <div className="absolute -bottom-10 -right-10 w-72 h-72 rounded-full bg-[color:var(--brand-orange)]/25 blur-3xl" />
          <div className="relative grid md:grid-cols-[1.4fr_1fr] gap-6 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                🤝 Become a Partner Store
              </div>
              <h2 className="mt-3 font-display text-2xl md:text-3xl font-extrabold leading-tight">
                Own a store? Offer in-store discounts, get paid instantly via Stripe.
              </h2>
              <ul className="mt-4 grid sm:grid-cols-3 gap-2 text-sm text-white/90">
                <li className="flex items-start gap-2"><span className="text-[color:var(--brand-orange)]">✓</span> Free to join</li>
                <li className="flex items-start gap-2"><span className="text-[color:var(--brand-orange)]">✓</span> Apple / Google Pay at your till</li>
                <li className="flex items-start gap-2"><span className="text-[color:var(--brand-orange)]">✓</span> Your own branded scan page</li>
              </ul>
            </div>
            <div className="md:justify-self-end">
              <Link
                to="/partner-program"
                data-testid="home-partner-cta"
                className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-6 py-3 shadow-card transition-colors"
              >
                Become a partner →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA rail */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-16" data-testid={HOME.featuredSection}>
        <div className="rounded-2xl bg-gradient-to-br from-[color:var(--brand-blue)] to-[#0F70A8] p-8 md:p-12 text-white relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-64 h-64 rounded-full bg-[color:var(--brand-orange)]/30 blur-3xl" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="max-w-xl">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold leading-tight">Got something to sell?</h2>
              <p className="mt-2 text-white/85 leading-relaxed text-sm md:text-base">
                Post a free ad in under a minute — our smart writer helps you craft a killer title and description.
              </p>
            </div>
            <Link
              to="/post-ad"
              data-testid={HOME.postAdBtn}
              className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold shadow-card transition-colors"
            >
              Post your ad
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function SectionListings({ slug, title }) {
  const q = useListingsFor(slug);
  const items = q.data || [];
  if (!q.isLoading && items.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 py-4">
      <SectionHeader title={title} seeAllHref={`/c/${slug}`} testId={`section-${slug}`} />
      <ListingGrid loading={q.isLoading} listings={items} empty=" " />
    </section>
  );
}
